import {
  getStartOfWeek,
  getEndOfWeek,
  formatDateToLocale,
  formatTimeToLocale,
  formatDateTimeToLocale,
  defaultLocale,
  defaultTimeZone
} from "./dateTimeFormat";

/**
 * Create a global timezone format hook. call setTimeZone when timezone is received, anywhere else use
 * the other functions to get the actual date converted to the settimezone
 * @returns timeformat hook
 */
const useTimeFormat = () => {
  /**
   * send in a datetime and get it back in the set timezone
   * @param {*} date
   * @returns date
   */
  const browserOptions = Intl?.DateTimeFormat().resolvedOptions();
  const useTimeFormatConfig = {
    timeZone: browserOptions?.timeZone || defaultTimeZone,
    locale: browserOptions?.locale || defaultLocale // to test another locale you could use "ko-KR" - the browser locale is ALWAYS used currently
  };

  const calculateDateTimeInZone = (date) => {
    if (useTimeFormatConfig.timeZone === "" || useTimeFormatConfig.timeZone === undefined) {
      // If no timezone was sent then return the date
      return date;
    }
    try {
      let value;
      if (typeof date === "string") {
        value = new Date(
          new Date(date).toLocaleString("en-US", {
            //useTimeFormatConfig.locale, { //
            timeZone: useTimeFormatConfig.timeZone
          })
        );
      } else {
        value = new Date(
          date.toLocaleString("en-US", {
            // useTimeFormatConfig.locale, { //
            timeZone: useTimeFormatConfig.timeZone
          })
        );
      }
      return value;
    } catch (e) {
      console.error("Error in calculateDateTimeInZone", e);
      console.error("Time zone config in use", useTimeFormatConfig);
    }
  };
  const getDateInTimeZone = (date) => {
    let value = calculateDateTimeInZone(date);
    value = value.toLocaleDateString(useTimeFormatConfig.locale);
    return value;
  };
  const getTimeInTimeZone = (date) => {
    let value = calculateDateTimeInZone(date);
    value = value.toLocaleTimeString(useTimeFormatConfig.locale);
    return value;
  };
  const getDateTimeInTimeZone = (date) => {
    let value = calculateDateTimeInZone(date);
    value = value.toLocaleString(useTimeFormatConfig.locale);
    return value;
  };
  const setTimeZone = (zone) => {
    if (zone === undefined) {
      return;
    }
    if (typeof zone !== "string") {
      console.error("Invalid Timezone recieved", zone);
      throw new error("A timezone must be a string");
    }
    useTimeFormatConfig.timeZone = zone;
  };
  const setLocale = (locale) => {
    useTimeFormatConfig.locale = locale;
  };
  const getTimeZone = () => {
    return useTimeFormatConfig.timeZone;
  };
  const getLocale = () => {
    return useTimeFormatConfig.locale;
  };

  /** Get date of Monday that has just happened */
  const getLastMondayInTimeZone = () => {
    // Get start of week - returns Date object
    // New implementation
    let t = new Date();
    return getStartOfWeek(t, useTimeFormatConfig.timeZone || defaultTimeZone);
  };

  /* Get sunday at 23:59:59:999 that is coming */
  const getComingSundayInTimeZone = () => {
    // Get end of week - returns Date object
    // New implementation
    let t = new Date();
    return getEndOfWeek(t, useTimeFormatConfig.timeZone || defaultTimeZone);
  };

  /** Formats date object to date time string using Locale */
  const formatDateTime = (date) => {
    // new implementation
    return formatDateTimeToLocale(date, useTimeFormatConfig.locale);
  };
  /** Formats date object to date string using Locale */
  const formatDate = (date) => {
    // new implementation
    return formatDateToLocale(date, useTimeFormatConfig.locale);
  };
  /** Formats date object to time string using Locale */
  const formatTime = (date) => {
    // new implementation
    return formatTimeToLocale(date, useTimeFormatConfig.locale);
  };

  const state = {
    setTimeZone: setTimeZone,
    getTimeZone: getTimeZone,
    setLocale: setLocale,
    getLocale: getLocale,
    getDateInTimeZone: getDateInTimeZone,
    getTimeInTimeZone: getTimeInTimeZone,
    getDateTimeInTimeZone: getDateTimeInTimeZone,
    calculateDateTimeInZone: calculateDateTimeInZone,
    getLastMondayInTimeZone: getLastMondayInTimeZone,
    getComingSundayInTimeZone: getComingSundayInTimeZone,
    formatDateTime,
    formatDate,
    formatTime
  };
  return state;
};

export default useTimeFormat;
